import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const LoginForm = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'worker',
    canScan: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // In a real app, this would be an API call to authenticate
      // For demo purposes, we'll simulate authentication
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Basic authentication logic for demo
      if (formData.username && formData.password) {
        // For demo purposes, accept any login with basic role assignment
        let role = 'worker';
        if (formData.username.toLowerCase().includes('admin')) role = 'admin';
        if (formData.username.toLowerCase().includes('super')) role = 'superuser';
        
        // Call the onLogin callback with user info
        onLogin({
          username: formData.username,
          password: formData.password,
          role: role,
          permissions: getPermissionsForRole(role, true)
        });
      } else {
        setError('Please enter both username and password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred during login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map roles to permissions
  const getPermissionsForRole = (role, canScan) => {
    switch (role) {
      case 'admin':
        return ['is_staff', 'can_scan'];
      case 'superuser':
        return ['is_staff', 'can_scan', 'is_superuser'];
      case 'worker':
        return canScan ? ['can_scan'] : [];
      default:
        return [];
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ 
          backgroundColor: 'primary.main', 
          borderRadius: '50%', 
          p: 1,
          mb: 2
        }}>
          <LockOutlinedIcon sx={{ color: 'white' }} />
        </Box>
        
        <Typography component="h1" variant="h5" gutterBottom>
          Staff Login
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={formData.username}
            onChange={handleChange}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              name="role"
              value={formData.role}
              label="Role"
              onChange={handleChange}
            >
              <MenuItem value="worker">Worker</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="developer">Developer</MenuItem>
              <MenuItem value="programmer">Programmer</MenuItem>
              <MenuItem value="it_manager">IT Manager</MenuItem>
              <MenuItem value="superuser">Super User</MenuItem>
            </Select>
          </FormControl>
          
          {formData.role === 'worker' && (
            <FormControlLabel
              control={
                <Checkbox
                  name="canScan"
                  checked={formData.canScan}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="Allow QR code scanning permission"
              sx={{ mt: 1, display: 'block' }}
            />
          )}
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Login'}
          </Button>
          
          <Typography variant="body2" color="text.secondary" align="center">
            For demo: Use admin/admin123, worker/worker123, developer/dev123, programmer/prog123, itmanager/it123, or superuser/super123
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default LoginForm; 